<template>
  <div class="task-calendar-container">
    <v-skeleton-loader :loading="loading" type="card-text">
      <v-card class="task-calendar">
        <v-card-text class="pa-2">
          <div class="calendar-header d-flex align-center">
            <v-btn v-if="showNavigation" icon small @click="navigateWeek(-1)">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>

            <div class="flex-grow-1 calendar-dates">
              <div class="date-row d-flex">
                <div
                  v-for="date in visibleDates"
                  :key="date.dateString"
                  class="date-column flex-grow-1"
                  :class="{ today: date.isToday }"
                >
                  <div
                    class="date-header text-center pa-2"
                    :class="{ 'today-header': date.isToday }"
                  >
                    <div class="date-day">
                      {{ date.isToday ? "Today" : date.dayName }}
                    </div>
                    <div class="date-number">
                      {{ date.monthName }} {{ date.dayNumber }}
                    </div>
                    <div
                      v-if="date.isToday && date.overdueCount > 0"
                      class="overdue-badge"
                    >
                      {{ date.overdueCount }} overdue
                    </div>
                  </div>
                  <div class="task-blocks">
                    <div
                      v-for="task in date.tasks"
                      :key="task.id"
                      class="task-block"
                      :class="getTaskBlockClass(task)"
                      @click="handleTaskClick(task, date)"
                    >
                      <div class="task-title">{{ task.title }}</div>
                      <div class="task-type">
                        {{ task.taskType?.name || "Task" }}
                      </div>
                      <div
                        v-if="task.overdue || task.isStackedOverdue"
                        class="task-overdue"
                      >
                        OVERDUE
                      </div>
                    </div>
                    <div v-if="date.tasks.length === 0" class="no-tasks">
                      No tasks
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <v-btn v-if="showNavigation" icon small @click="navigateWeek(1)">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-skeleton-loader>
    <DailyChecklistModal
      v-if="checklistDialog && selectedTask"
      v-model="checklistDialog"
      :date="selectedDate"
      :task="selectedTask"
      @task-updated="handleTaskUpdate"
    />
  </div>
</template>

<script>
import { DateTime } from "luxon";
import DailyChecklistModal from "./DialogChecklistModal.vue";

export default {
  name: "TaskCalendar",
  components: {
    DailyChecklistModal,
  },
  props: {
    dateRange: {
      type: Object,
      required: true,
    },
    tasks: {
      type: Array,
      default: () => [],
    },
    timeZone: {
      type: String,
      default: "UTC",
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      checklistDialog: false,
      selectedDate: null,
      selectedTask: null,
      currentWeekOffset: 0,
    };
  },
  computed: {
    dateRangeStart() {
      if (!this.dateRange.after) {
        return DateTime.now({ zone: this.timeZone }).startOf("day");
      }
      const parsed = DateTime.fromISO(this.dateRange.after);
      const result = DateTime.fromObject(
        {
          year: parsed.year,
          month: parsed.month,
          day: parsed.day,
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        },
        { zone: this.timeZone }
      );

      return result;
    },
    dateRangeEnd() {
      return this.dateRange.before
        ? DateTime.fromISO(this.dateRange.before, {
            zone: this.timeZone,
          }).endOf("day")
        : DateTime.now({ zone: this.timeZone }).endOf("day");
    },
    totalDays() {
      return Math.ceil(
        this.dateRangeEnd.diff(this.dateRangeStart, "days").days
      );
    },
    showNavigation() {
      return this.totalDays > 7;
    },
    allDates() {
      const dates = [];
      let current = this.dateRangeStart;
      const endDate = this.dateRangeEnd.startOf("day");
      const today = DateTime.now({ zone: this.timeZone }).startOf("day");

      while (current <= endDate) {
        const tasksForDate = this.getTasksForDate(current);
        const isToday = current.hasSame(today, "day");
        const overdueCount = isToday ? this.getOverdueTasksCount() : 0;

        dates.push({
          date: current,
          dateString: current.toISODate(),
          dayName: current.toFormat("ccc"),
          dayNumber: current.toFormat("dd"),
          monthName: current.toFormat("MMM"),
          tasks: tasksForDate,
          isToday: isToday,
          overdueCount: overdueCount,
        });
        current = current.plus({ days: 1 });
      }
      return dates;
    },
    visibleDates() {
      if (!this.showNavigation) {
        return this.allDates;
      }
      const startIndex = this.currentWeekOffset * 7;
      return this.allDates.slice(startIndex, startIndex + 7);
    },
    maxWeekOffset() {
      return Math.max(0, Math.ceil(this.allDates.length / 7) - 1);
    },
  },
  watch: {
    dateRange: {
      handler(newDateRange, oldDateRange) {
        if (
          oldDateRange &&
          (newDateRange.after !== oldDateRange.after ||
            newDateRange.before !== oldDateRange.before)
        ) {
          this.currentWeekOffset = 0;
        }
      },
      deep: true,
    },
    totalDays: {
      handler(newTotalDays) {
        const maxOffset = Math.max(0, Math.ceil(newTotalDays / 7) - 1);
        if (this.currentWeekOffset > maxOffset) {
          this.currentWeekOffset = 0;
        }
      },
    },
  },
  methods: {
    getTasksForDate(date) {
      const today = DateTime.now({ zone: this.timeZone }).startOf("day");
      const calendarDate = date.startOf("day");
      const isToday = calendarDate.hasSame(today, "day");

      return this.tasks.filter((task) => {
        if (!task.dueAt) {
          return false;
        }

        // For overdue tasks stacked on today
        if (task.isStackedOverdue && isToday) {
          return true;
        }

        // For regular tasks
        const taskDate = DateTime.fromISO(task.dueAt, {
          zone: this.timeZone,
        }).startOf("day");

        return taskDate.isValid && taskDate.hasSame(calendarDate, "day");
      });
    },
    getOverdueTasksCount() {
      return this.tasks.filter((task) => task.isStackedOverdue || task.overdue)
        .length;
    },
    hasChecklist(task) {
      return (
        task.actions?.checklist &&
        Array.isArray(task.actions.checklist) &&
        task.actions.checklist.length > 0
      );
    },
    getTaskBlockClass(task) {
      const taskType = task.taskType?.name?.toLowerCase() || "";
      const baseClass = "mb-1";
      const isOverdue = task.overdue || task.isStackedOverdue;

      if (this.hasChecklist(task)) {
        return `${baseClass} checklist-task ${isOverdue ? "overdue" : ""}`;
      }

      if (taskType.includes("walkaround")) {
        return `${baseClass} walkaround-task ${isOverdue ? "overdue" : ""}`;
      }

      if (taskType.includes("custom")) {
        return `${baseClass} custom-task ${isOverdue ? "overdue" : ""}`;
      }

      if (taskType.includes("incident")) {
        return `${baseClass} incident-resolution ${isOverdue ? "overdue" : ""}`;
      }

      return `${baseClass} default-task ${isOverdue ? "overdue" : ""}`;
    },
    handleTaskClick(task, date) {
      if (this.hasChecklist(task)) {
        this.selectedDate = date.date;
        this.selectedTask = task;
        this.checklistDialog = true;
      } else {
        this.$emit("open-task-dialog", task);
      }
    },
    navigateWeek(direction) {
      const newOffset = this.currentWeekOffset + direction;
      if (newOffset >= 0 && newOffset <= this.maxWeekOffset) {
        this.currentWeekOffset = newOffset;
      }
    },
    handleTaskUpdate(updateData) {
      this.$emit("task-updated", updateData);
    },
  },
};
</script>

<style scoped>
.task-calendar-container {
  margin-top: 12px;
}

.task-calendar {
  border-radius: 8px;
}

.calendar-header {
  min-height: 200px;
}

.date-row {
  gap: 8px;
}

.date-column {
  min-height: 180px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #fafafa;
}

.date-column:hover {
  background-color: #f5f5f5;
  border-color: #1976d2;
}

.date-column.today {
  border-color: #2196f3;
  background-color: #e3f2fd;
}

.date-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}

.date-header.today-header {
  background-color: #2196f3;
  color: white;
}

.date-day {
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  font-weight: 500;
}

.today-header .date-day,
.today-header .date-number {
  color: white;
}

.date-number {
  font-size: 16px;
  font-weight: 600;
}

.overdue-badge {
  font-size: 10px;
  color: #d32f2f;
  font-weight: bold;
  background-color: rgba(211, 47, 47, 0.1);
  padding: 2px 4px;
  border-radius: 2px;
  margin-top: 2px;
}

.today-header .overdue-badge {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
}

.task-blocks {
  padding: 4px;
  max-height: 120px;
  overflow-y: auto;
}

.task-block {
  padding: 4px 6px;
  border-radius: 3px;
  font-size: 11px;
  line-height: 1.2;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 4px;
}

.task-block:hover {
  opacity: 0.8;
  transform: translateY(-1px);
}

.calendar-dates {
  overflow-x: auto;
}

.task-title {
  font-weight: 500;
  width: 100px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.task-type {
  color: #666;
  font-size: 10px;
}

.task-overdue {
  color: #d32f2f;
  font-size: 9px;
  font-weight: bold;
  text-transform: uppercase;
}

.no-tasks {
  color: #999;
  font-size: 11px;
  text-align: center;
  padding: 8px;
  font-style: italic;
}

/* Task type colors */
.checklist-task {
  background-color: #e3f2fd;
  color: #1976d2;
  border-left: 3px solid #1976d2;
}

.walkaround-task {
  background-color: #e8f5e8;
  color: #388e3c;
  border-left: 3px solid #388e3c;
}

.custom-task {
  background-color: #fce4ec;
  color: #c2185b;
  border-left: 3px solid #c2185b;
}

.incident-resolution {
  background-color: #fff3e0;
  color: #f57c00;
  border-left: 3px solid #f57c00;
}

.default-task {
  background-color: #f5f5f5;
  color: #424242;
  border-left: 3px solid #424242;
}

.task-block.overdue {
  background-color: #ffebee !important;
  color: #d32f2f !important;
  border-left: 3px solid #d32f2f !important;
}
</style>
